import React, { useState } from "react";
import { FAQS } from "../data/site";
import { ChevronDown } from "lucide-react";

/**
 * FAQ section — Global Assets typography:
 *   • "Frequently Asked Questions." → Karvest Trial Regular, opacity 65%
 *   • "Everything you need to know…" subheading → Karvest Trial Regular
 *   • Question text → SF Pro Display Medium (500)
 *   • Answer text (base) → SF Pro Display Regular (400)
 *   • Answer emphasis (**bold** words) → SF Pro Display Bold (700)
 */

// Turn simple markdown-style **bold** into <strong>
function renderAnswer(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\n)/g);
  return parts.map((p, i) => {
    if (p === "\n") return <br key={i} />;
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong
          key={i}
          style={{
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 700,
            color: "#FFFFFF",
          }}
        >
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

function Item({ q, a, isOpen, onToggle }) {
  return (
    <div className="reveal glass-card overflow-hidden">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between text-left px-5 md:px-6 py-5 md:py-6 group"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full bg-white/70 shrink-0" />
          <span
            className="text-white"
            style={{
              fontFamily: "'SF Pro Display',sans-serif",
              fontWeight: 500,
              fontSize: "clamp(14px, 1.15vw, 17px)",
              letterSpacing: "-0.005em",
              lineHeight: 1.35,
            }}
          >
            {q}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-white/60 transition-transform duration-300 shrink-0 ml-3 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className="overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-out"
        style={{ maxHeight: isOpen ? 500 : 0, opacity: isOpen ? 1 : 0 }}
      >
        <div
          className="px-6 md:px-8 pb-6 md:pb-7"
          style={{
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(13px, 1vw, 15px)",
            lineHeight: 1.6,
            color: "rgba(255,255,255,0.78)",
          }}
        >
          {renderAnswer(a)}
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-6 md:py-14">
      <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
        <h2
          className="reveal text-transparent bg-clip-text"
          style={{
            fontFamily: "'Clash Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(54px, 8vw, 84px)",
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            backgroundImage:
              "linear-gradient(180deg,#EDE9FE 0%,#C4B5FD 50%,#8B5CF6 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          FAQ
        </h2>

        {/* Small heading — Karvest Trial Regular, opacity 65% */}
        <div
          className="reveal mt-2"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(18px, 2vw, 28px)",
            lineHeight: 1.2,
            letterSpacing: "0.005em",
            color: "rgba(255,255,255,0.65)",
          }}
        >
          Frequently Asked Questions.
        </div>

        {/* Subheading — Karvest Trial Regular */}
        <p
          className="reveal mt-3"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(13px, 1vw, 17px)",
            lineHeight: 1.5,
            letterSpacing: "0.005em",
            color: "rgba(255,255,255,0.78)",
          }}
        >
          Everything you need to know before starting your
          <br />
          project with Clickory.
        </p>

        <div className="mt-10 md:mt-14 space-y-4 text-left">
          {FAQS.map((f, i) => (
            <Item
              key={f.q}
              q={f.q}
              a={f.a}
              isOpen={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
